import { useEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '../../design-system/icons';
import { dsColors } from '../../design-system/tokens/colors';
import { useIsDesktop } from './useIsDesktop';

/**
 * Contenedor responsive de los sheets de auth del onboarding (ver ADR-704).
 *
 * - Mobile: bottom sheet blanco con esquinas superiores redondeadas (radius-lg),
 *   que entra deslizándose de abajo hacia arriba sobre un scrim que atenúa el
 *   carrusel.
 * - Desktop (`lg:`): el mismo contenido pasa a ocupar el panel izquierdo (mitad
 *   blanca, contenido centrado vertical y alineado a la izquierda) mientras la
 *   imagen del carrusel sigue a la derecha — patrón split de la referencia xAI.
 *
 * Accesibilidad: `role="dialog"` + `aria-modal`, foco inicial dentro del panel,
 * trap de Tab, ESC para cerrar, cierre por scrim (mobile) y bloqueo de scroll del
 * body mientras está abierto.
 */

const ANIM_MS = 300;

export interface AuthSheetProps {
  open: boolean;
  onClose: () => void;
  /** id del título del sheet, para `aria-labelledby`. */
  titleId: string;
  children: ReactNode;
}

export function AuthSheet({ open, onClose, titleId, children }: AuthSheetProps) {
  // Desktop: la ventana de auth es **fija** (no se puede cerrar): sin X, sin ESC, sin
  // cierre por scrim. En mobile es un bottom sheet cerrable.
  const isDesktop = useIsDesktop();
  const dismissable = !isDesktop;

  // `mounted` mantiene el DOM durante la animación de salida; `shown` dispara la
  // transición (entrada/salida).
  const [mounted, setMounted] = useState(open);
  const [shown, setShown] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) {
      setMounted(true);
      // Dos frames: montar → pintar estado inicial (fuera de pantalla) → animar.
      const r = requestAnimationFrame(() => requestAnimationFrame(() => setShown(true)));
      return () => cancelAnimationFrame(r);
    }
    setShown(false);
    const t = setTimeout(() => setMounted(false), ANIM_MS);
    return () => clearTimeout(t);
  }, [open]);

  // Bloqueo de scroll del body + foco inicial mientras está montado.
  useEffect(() => {
    if (!mounted) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const focusTimer = setTimeout(() => {
      const panel = panelRef.current;
      if (!panel) return;
      // Enfocamos el PANEL (contenedor `tabindex=-1`), NO el primer input: al abrir, el
      // foco entra al diálogo (a11y: trap de Tab + anuncio del screen reader) pero ningún
      // campo queda resaltado con el ring de focus ni se abre el teclado solo en mobile.
      panel.focus({ preventScroll: true });
    }, ANIM_MS);
    return () => {
      document.body.style.overflow = prevOverflow;
      clearTimeout(focusTimer);
    };
  }, [mounted]);

  // ESC para cerrar + trap de Tab dentro del panel.
  useEffect(() => {
    if (!mounted) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (dismissable) onClose();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const focusables = panel.querySelectorAll<HTMLElement>(
        'input:not([disabled]), button:not([disabled]), [href], select, textarea, [tabindex]:not([tabindex="-1"])',
      );
      if (focusables.length === 0) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [mounted, onClose, dismissable]);

  if (!mounted) return null;

  return createPortal(
    <div
      // `pointer-events-none` en el contenedor full-screen: en desktop el sheet está
      // SIEMPRE abierto y este div cubre toda la pantalla (`inset-0`) por encima del
      // carrusel (z-100). Sin esto, "tragaría" los clicks sobre la imagen (mitad
      // derecha) y el carrusel no se podría navegar. Se reactivan los eventos sólo en
      // el scrim (mobile) y el panel, así la mitad derecha deja pasar el click al carrusel.
      className="pointer-events-none fixed inset-0 z-[100] font-sans"
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
    >
      {/* Scrim (sólo mobile: en desktop la imagen del carrusel queda visible a la derecha) */}
      <button
        type="button"
        aria-label="Cerrar"
        tabIndex={-1}
        onClick={onClose}
        className={`pointer-events-auto absolute inset-0 cursor-default bg-black/50 transition-opacity duration-300 ease-out lg:hidden ${
          shown ? 'opacity-100' : 'opacity-0'
        }`}
      />

      {/* Panel.
          Mobile: tarjeta flotante a 20px de los bordes laterales y del fondo
          (safe-area incluida), con las 4 esquinas redondeadas (radius-lg).
          Desktop (`lg:`): panel izquierdo full-height sin inset ni redondeo. */}
      <div
        ref={panelRef}
        // `tabIndex={-1}` + `outline-none`: el panel es enfocable por código (foco inicial
        // del diálogo) pero sin resaltar el borde con outline.
        tabIndex={-1}
        className={`pointer-events-auto absolute inset-x-5 bottom-[calc(env(safe-area-inset-bottom)+20px)] flex max-h-[88dvh] flex-col overflow-y-auto rounded-[16px] shadow-[0_10px_40px_rgba(0,0,0,0.25)] outline-none transition-[transform,opacity] duration-300 ease-out lg:inset-x-0 lg:inset-y-0 lg:left-0 lg:right-auto lg:max-h-none lg:w-1/2 lg:rounded-none lg:shadow-none ${
          shown
            ? 'translate-y-0 opacity-100'
            : 'translate-y-full opacity-0 lg:translate-y-0'
        }`}
        style={{ backgroundColor: dsColors.base.white }}
      >
        {/* Botón cerrar: sólo en mobile (bottom sheet cerrable). En desktop la
            ventana es fija (no se renderiza la X). Su eje vertical coincide con el
            del título (top 20px + alto = línea del título de 25px). */}
        {dismissable && (
          <button
            type="button"
            onClick={onClose}
            aria-label="Cerrar"
            className="absolute right-5 top-5 z-10 flex h-[25px] w-6 items-center justify-center rounded-full transition-colors hover:bg-neutral-100 lg:hidden"
            style={{ color: dsColors.neutral[900] }}
          >
            <Icon name="outline/cross" size={18} />
          </button>
        )}
        {children}
      </div>
    </div>,
    document.body,
  );
}

export default AuthSheet;
